/**
 * TamamPreview.jsx
 * ----------------
 * VISUAL MOCKUP PAGE for the upcoming "تمام الصلاة" (Tamam al-Salah) feature.
 *
 * This is a PREVIEW ONLY — no logic wired yet. It renders the 6 designed
 * screens inside phone frames on a scrollable page so the user can review
 * spacing, colours, quadrant layout and copy before we implement the real
 * feature.
 *
 * Route: /tamam-preview
 */

import React, { useEffect, useState } from 'react';
import { useAstronomicalData } from '@/hooks/useAstronomicalData';
import { getCelestialBackground, getTextColors, getSurfaceColors } from '@/utils/celestialBackground';
import { Moon, ArrowUp, Info } from 'lucide-react';

const PHONE_W = 300;
const PHONE_H = 620;
const CIRCLE_SIZE = 76; // was 60 — bumped per user feedback
const CIRCLE_OPACITY = 0.42; // was 0.85 — softer contrast per user feedback
const DIM_OVERLAY = 0.15; // default night dimming; user-adjustable in real feature

/* ---------------------------------------------------------- Phone frame */
const PhoneFrame = ({ children, label, note }) => (
  <div className="flex flex-col items-center gap-3">
    <div
      className="rounded-[38px] p-2"
      style={{
        width: PHONE_W + 20,
        background: 'linear-gradient(145deg, #1a1f2e 0%, #0d1119 100%)',
        boxShadow: '0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.05)',
      }}
    >
      <div
        className="rounded-[30px] overflow-hidden relative"
        style={{ width: PHONE_W, height: PHONE_H }}
      >
        {/* Notch */}
        <div
          className="absolute top-2 left-1/2 -translate-x-1/2 z-30 rounded-full"
          style={{ width: 90, height: 20, background: '#0d1119' }}
        />
        {children}
      </div>
    </div>
    <div className="text-center max-w-[300px]">
      <div className="text-heading text-base text-white/90 mb-1">{label}</div>
      {note && <div className="text-body text-xs text-white/60 leading-relaxed">{note}</div>}
    </div>
  </div>
);

/* ---------------------------------------------------------- Screen 2  */
/* Welcome / instructions overlay (first-time entry point of the flow)  */
const Screen2_Welcome = ({ celestialStyle, textColors, surfaceColors }) => (
  <div
    className="w-full h-full flex items-center justify-center p-4"
    style={{ background: celestialStyle.background }}
    dir="rtl"
  >
    <div
      className="rounded-3xl p-5 w-full"
      style={{
        background: surfaceColors.card,
        border: `1px solid ${surfaceColors.border}`,
        backdropFilter: 'blur(20px)',
      }}
    >
      <div className="text-center mb-4">
        <Moon size={28} color={textColors.accent} className="mx-auto mb-2" />
        <div className="text-heading text-lg" style={{ color: textColors.primary }}>
          تمام الصلاة
        </div>
      </div>

      {/* Mini demo of the quadrants */}
      <div
        className="rounded-xl p-3 mb-4 relative"
        style={{
          background: 'rgba(0,0,0,0.25)',
          height: 110,
          border: `1px dashed ${surfaceColors.border}`,
        }}
      >
        <div className="absolute inset-3 grid grid-cols-2 grid-rows-2 gap-0">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-center">
              <div
                className="rounded-full"
                style={{
                  width: 22,
                  height: 22,
                  background: textColors.primary,
                  opacity: 0.35,
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <p className="text-[11px] mb-3 leading-relaxed text-center" style={{ color: textColors.secondary }}>
        عند اللمس تظهر دائرة تُشير إلى ركعة أُدّيت — أربع نقاط لأربع ركعات
      </p>

      <ul className="text-[11px] mb-3 space-y-1" style={{ color: textColors.primary }}>
        <li>✦ ضع الهاتف يمين أو يسار موضع سجودك</li>
        <li>✦ الْمَس الشاشة بعد كل ركعة</li>
        <li>✦ للخروج: اسحب من الأسفل إلى الأعلى</li>
      </ul>

      {/* Brightness control — new */}
      <div
        className="rounded-lg p-2.5 mb-3"
        style={{
          background: 'rgba(255,255,255,0.05)',
          border: `1px solid ${surfaceColors.border}`,
        }}
      >
        <div className="text-[10px] mb-2" style={{ color: textColors.secondary }}>
          شدّة الإضاءة
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {[
            { key: 'dim', label: 'خافتة', dot: 0.15 },
            { key: 'med', label: 'متوسطة', dot: 0.42, active: true },
            { key: 'bright', label: 'مضيئة', dot: 0.85 },
          ].map((opt) => (
            <div
              key={opt.key}
              className="rounded-md py-1.5 text-center text-[10px] flex flex-col items-center gap-1"
              style={{
                background: opt.active ? `${textColors.accent}22` : 'transparent',
                border: opt.active ? `1px solid ${textColors.accent}66` : `1px solid ${surfaceColors.border}`,
                color: textColors.primary,
              }}
            >
              <div
                className="rounded-full"
                style={{
                  width: 10,
                  height: 10,
                  background: textColors.primary,
                  opacity: opt.dot,
                }}
              />
              <span>{opt.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div
        className="rounded-lg p-2 mb-3 flex items-start gap-2"
        style={{
          background: 'rgba(245, 158, 11, 0.12)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
        }}
      >
        <Info size={13} color="#F59E0B" className="mt-0.5 flex-shrink-0" />
        <div className="text-[10px]" style={{ color: textColors.primary }}>
          يُنصح بتفعيل &quot;عدم الإزعاج&quot; قبل البدء
        </div>
      </div>

      {/* Don't show again */}
      <label
        className="flex items-center gap-2 mb-3 cursor-pointer"
        style={{ color: textColors.secondary }}
      >
        <input
          type="checkbox"
          className="w-3.5 h-3.5 accent-current"
          style={{ accentColor: textColors.accent }}
          readOnly
        />
        <span className="text-[10px]">لا تعرض هذه الشاشة مرة أخرى</span>
      </label>

      <button
        className="w-full py-3 rounded-xl text-heading text-sm"
        style={{
          background: `linear-gradient(135deg, ${textColors.accent}, ${textColors.accent}dd)`,
          color: '#0d1119',
        }}
      >
        ابدأ الصلاة
      </button>
    </div>
  </div>
);

/* ---------------------------------------------------------- Screen 3  */
/* Empty prayer session — pure blank screen ready to receive touches    */
const Screen3_Empty = ({ celestialStyle }) => (
  <div
    className="w-full h-full relative"
    style={{
      background: celestialStyle.background,
    }}
  >
    <div
      className="absolute inset-0"
      style={{ background: `rgba(0,0,0,${DIM_OVERLAY})` }}
    />
    {/* Subtle grid guide (visible in preview only, hidden in real app) */}
    <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
      <div className="w-full h-full grid grid-cols-2 grid-rows-2">
        <div className="border-l border-b border-white" />
        <div className="border-b border-white" />
        <div className="border-l border-white" />
        <div />
      </div>
    </div>
  </div>
);

/* ---------------------------------------------------------- Screen 4  */
/* One rakat done — softened dark circle in top-right (RTL first quad)  */
const Screen4_OneRakat = ({ celestialStyle, textColors }) => (
  <div
    className="w-full h-full relative"
    style={{ background: celestialStyle.background }}
  >
    <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${DIM_OVERLAY})` }} />
    <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
      <div className="w-full h-full grid grid-cols-2 grid-rows-2">
        <div className="border-l border-b border-white" />
        <div className="border-b border-white" />
        <div className="border-l border-white" />
        <div />
      </div>
    </div>
    <div
      className="absolute rounded-full"
      style={{
        width: CIRCLE_SIZE,
        height: CIRCLE_SIZE,
        top: PHONE_H / 4 - CIRCLE_SIZE / 2,
        right: PHONE_W / 4 - CIRCLE_SIZE / 2,
        background: textColors.primary === '#FFFFFF' ? '#0d1119' : '#FFFFFF',
        opacity: CIRCLE_OPACITY,
      }}
    />
  </div>
);

/* ---------------------------------------------------------- Screen 5  */
/* Four rakats done — all quadrants filled                              */
const Screen5_FourRakats = ({ celestialStyle, textColors }) => {
  const circleStyle = {
    width: CIRCLE_SIZE,
    height: CIRCLE_SIZE,
    background: textColors.primary === '#FFFFFF' ? '#0d1119' : '#FFFFFF',
    opacity: CIRCLE_OPACITY,
  };
  return (
    <div
      className="w-full h-full relative"
      style={{ background: celestialStyle.background }}
    >
      <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${DIM_OVERLAY})` }} />
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
        <div className="w-full h-full grid grid-cols-2 grid-rows-2">
          <div className="border-l border-b border-white" />
          <div className="border-b border-white" />
          <div className="border-l border-white" />
          <div />
        </div>
      </div>
      <div className="absolute rounded-full" style={{ ...circleStyle, top: PHONE_H / 4 - CIRCLE_SIZE / 2, right: PHONE_W / 4 - CIRCLE_SIZE / 2 }} />
      <div className="absolute rounded-full" style={{ ...circleStyle, top: PHONE_H / 4 - CIRCLE_SIZE / 2, left: PHONE_W / 4 - CIRCLE_SIZE / 2 }} />
      <div className="absolute rounded-full" style={{ ...circleStyle, bottom: PHONE_H / 4 - CIRCLE_SIZE / 2, right: PHONE_W / 4 - CIRCLE_SIZE / 2 }} />
      <div className="absolute rounded-full" style={{ ...circleStyle, bottom: PHONE_H / 4 - CIRCLE_SIZE / 2, left: PHONE_W / 4 - CIRCLE_SIZE / 2 }} />
    </div>
  );
};

/* ---------------------------------------------------------- Screen 6  */
/* Exit gesture — swipe up from bottom                                  */
const Screen6_Exit = ({ celestialStyle, textColors }) => {
  const circleStyle = {
    width: CIRCLE_SIZE,
    height: CIRCLE_SIZE,
    background: textColors.primary === '#FFFFFF' ? '#0d1119' : '#FFFFFF',
    opacity: CIRCLE_OPACITY,
  };
  return (
    <div
      className="w-full h-full relative"
      style={{ background: celestialStyle.background }}
    >
      <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${DIM_OVERLAY})` }} />
      <div className="absolute rounded-full" style={{ ...circleStyle, top: PHONE_H / 4 - CIRCLE_SIZE / 2, right: PHONE_W / 4 - CIRCLE_SIZE / 2 }} />
      <div className="absolute rounded-full" style={{ ...circleStyle, top: PHONE_H / 4 - CIRCLE_SIZE / 2, left: PHONE_W / 4 - CIRCLE_SIZE / 2 }} />
      <div className="absolute rounded-full" style={{ ...circleStyle, bottom: PHONE_H / 4 - CIRCLE_SIZE / 2, right: PHONE_W / 4 - CIRCLE_SIZE / 2 }} />
      <div className="absolute rounded-full" style={{ ...circleStyle, bottom: PHONE_H / 4 - CIRCLE_SIZE / 2, left: PHONE_W / 4 - CIRCLE_SIZE / 2 }} />

      <div
        className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        style={{ bottom: 60 }}
      >
        <div className="animate-bounce">
          <ArrowUp size={32} color={textColors.accent} strokeWidth={2.5} />
        </div>
        <div
          className="text-[11px] px-3 py-1.5 rounded-full"
          style={{
            background: 'rgba(0,0,0,0.6)',
            color: '#fff',
            backdropFilter: 'blur(10px)',
          }}
        >
          اسحب لأعلى للخروج
        </div>
      </div>

      <div
        className="absolute left-1/2 -translate-x-1/2 w-1 rounded-full opacity-30"
        style={{
          bottom: 100,
          height: 80,
          background: `linear-gradient(to top, ${textColors.accent}, transparent)`,
        }}
      />
    </div>
  );
};

/* ---------------------------------------------------------- Main page */
export default function TamamPreview() {
  const [useGPS] = useState(true);
  // Force RTL for preview since this is Arabic-only content
  useEffect(() => {
    document.documentElement.dir = 'rtl';
    document.documentElement.lang = 'ar';
  }, []);

  const { astronomicalData } = useAstronomicalData(null, useGPS);
  const sunPosition = astronomicalData?.sun_position;
  const celestialStyle = getCelestialBackground(sunPosition);
  const textColors = getTextColors(sunPosition?.phase || 'night');
  const surfaceColors = getSurfaceColors(sunPosition?.phase || 'night');

  const screens = [
    {
      key: 's2',
      label: '١ — شاشة الترحيب',
      note: 'أول شاشة عند فتح الميزة — تعليمات + متحكّم شدّة الإضاءة + خيار عدم الإظهار',
      render: <Screen2_Welcome celestialStyle={celestialStyle} textColors={textColors} surfaceColors={surfaceColors} />,
    },
    {
      key: 's3',
      label: '٢ — الشاشة الرئيسيّة (فارغة)',
      note: 'خلفية فلك الديناميّة + تعتيم ١٥٪ — الخطوط المتقطّعة تظهر في المعاينة فقط',
      render: <Screen3_Empty celestialStyle={celestialStyle} />,
    },
    {
      key: 's4',
      label: '٣ — بعد الركعة الأولى',
      note: `دائرة (${CIRCLE_SIZE} بكسل، شفافيّة ${Math.round(CIRCLE_OPACITY * 100)}٪) — نعومة أكثر لتقليل التشتيت`,
      render: <Screen4_OneRakat celestialStyle={celestialStyle} textColors={textColors} />,
    },
    {
      key: 's5',
      label: '٤ — بعد أربع ركعات',
      note: 'الترتيب: يمين علوي ← يسار علوي ← يمين سفلي ← يسار سفلي (RTL)',
      render: <Screen5_FourRakats celestialStyle={celestialStyle} textColors={textColors} />,
    },
    {
      key: 's6',
      label: '٥ — إيماءة الخروج',
      note: 'سحب من الأسفل إلى الأعلى — عكس اتجاه Pull-to-Refresh لتجنّب الخطأ',
      render: <Screen6_Exit celestialStyle={celestialStyle} textColors={textColors} />,
    },
  ];

  return (
    <div
      className="min-h-screen py-10 px-4"
      style={{ background: celestialStyle.background }}
      dir="rtl"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div
            className="inline-block px-4 py-1.5 rounded-full text-xs mb-4"
            style={{
              background: 'rgba(245, 158, 11, 0.15)',
              border: '1px solid rgba(245, 158, 11, 0.4)',
              color: '#F59E0B',
            }}
            data-testid="preview-badge"
          >
            معاينة تصميم — قبل التنفيذ
          </div>
          <h1 className="text-heading text-4xl md:text-5xl mb-3" style={{ color: textColors.primary }}>
            تمام الصلاة
          </h1>
          <p className="text-body text-sm md:text-base max-w-xl mx-auto" style={{ color: textColors.secondary }}>
            جولة بصريّة كاملة لكل شاشة قبل بدء البرمجة الفعليّة —
            الخلفيّة الديناميّة الحاليّة لفلك مطبّقة على المعاينة
          </p>
        </div>

        {/* Screens grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10 justify-items-center">
          {screens.map((s) => (
            <PhoneFrame key={s.key} label={s.label} note={s.note}>
              {s.render}
            </PhoneFrame>
          ))}
        </div>

        {/* Spec table */}
        <div
          className="mt-16 rounded-2xl p-6 max-w-3xl mx-auto"
          style={{
            background: surfaceColors.card,
            border: `1px solid ${surfaceColors.border}`,
            backdropFilter: 'blur(20px)',
          }}
        >
          <h2 className="text-heading text-xl mb-4" style={{ color: textColors.primary }}>
            المواصفات المُقرّة
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" style={{ color: textColors.primary }}>
            {[
              ['الاسم', 'تمام الصلاة'],
              ['عدد الأقسام (المرحلة 1)', '٤ ركعات'],
              ['عدد الأقسام (رمضان القادم)', '٨ ركعات — للتراويح'],
              ['لون الخلفيّة', 'خلفيّة فلك الديناميّة + تعتيم قابل للتخصيص'],
              ['شدّة الإضاءة', '٣ درجات: خافتة / متوسطة / مضيئة'],
              ['لون الدائرة', 'متباين ناعم مع الخلفيّة (شفافيّة ٤٢٪ افتراضياً)'],
              ['حجم الدائرة', `${CIRCLE_SIZE} بكسل`],
              ['فترة التعطيل', '٢٥ ثانية بعد كل لمسة'],
              ['نقطة الدخول', 'داخل بطاقة مواقيت الصلاة (متاح للنوافل بين الفروض)'],
              ['شاشة الترحيب', 'بخيار "لا تعرض هذه الشاشة مرة أخرى"'],
              ['اتجاه العدّ', 'يتبع اتجاه اللغة (RTL / LTR)'],
              ['إبقاء الشاشة مضاءة', 'Wake Lock API'],
              ['كتم النظام', 'يدوي — عبر زر يفتح إعدادات "عدم الإزعاج"'],
              ['الخروج', 'سحب لأعلى ≥ ٤٠٪ من ارتفاع الشاشة'],
              ['اللغات', '١٣ لغة'],
              ['الإصدار', 'مجاني — ضمن فلك الأساسي'],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-2 border-b" style={{ borderColor: surfaceColors.border }}>
                <span style={{ color: textColors.secondary }}>{k}</span>
                <span className="text-heading text-xs md:text-sm">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer note */}
        <div className="mt-10 text-center">
          <a
            href="/"
            className="inline-block text-sm underline"
            style={{ color: textColors.accent }}
            data-testid="back-to-app-link"
          >
            ← العودة إلى فلك
          </a>
        </div>
      </div>
    </div>
  );
}
